import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { SOCKET_URL, EXCLUDED_ROUTES, MODAL_TIMEOUT, MODAL_TYPES } from "../config/constants";
import { getAdminProfileData } from "../utils/cookieUtils";

export const useSocketNotifications = ({ hasInteracted, isTabActive, playSuccessSound, playErrorSound }) => {
  const [notificationState, setNotificationState] = useState({
    successModalOpen: false,
    errorModalOpen: false,
    user: {
      fname: "",
      lname: "",
      profileImage: "",
      id: "",
    },
  });

  const location = useLocation();
  const socketRef = useRef(null);
  const timeoutRef = useRef(null);

  // Initialize socket connection
  useEffect(() => {
    socketRef.current = io(SOCKET_URL);
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const closeModals = useCallback(() => {
    setNotificationState(prev => ({
      ...prev,
      successModalOpen: false,
      errorModalOpen: false,
    }));
  }, []);

  const handleClockInNotify = useCallback((message) => {
    const adminProfileData = getAdminProfileData();
    
    if (!adminProfileData || !adminProfileData.message || !adminProfileData.message.admin) {
      console.warn("Admin profile data not found in cookies");
      return;
    }

    const adminLocationId = adminProfileData.message.admin.location_id;
    const messageLocationId = message.location_id || message.user?.location_id;

    if (adminLocationId !== messageLocationId) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const userData = {
      fname: message.user.f_name,
      lname: message.user.l_name,
      profileImage: message.user.profile_image,
      id: message.user.id,
      location: message.user.location_id,
    };

    setNotificationState(prev => ({
      ...prev,
      user: userData,
      successModalOpen: message.type === MODAL_TYPES.SUCCESS,
      errorModalOpen: message.type === MODAL_TYPES.ERROR,
    }));

    if (message.type === MODAL_TYPES.SUCCESS) {
      playSuccessSound();
    } else if (message.type === MODAL_TYPES.ERROR) {
      playErrorSound();
    }

    // Auto-close modal after timeout
    timeoutRef.current = setTimeout(() => {
      closeModals();
    }, MODAL_TIMEOUT);
  }, [playSuccessSound, playErrorSound, closeModals]);

  useEffect(() => {
    const socket = socketRef.current;
    const shouldListen = !EXCLUDED_ROUTES.includes(location.pathname) && isTabActive;

    if (socket && shouldListen) {
      socket.on("clock-in-notify", handleClockInNotify);
    }

    return () => {
      if (socket) {
        socket.off("clock-in-notify", handleClockInNotify);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location.pathname, isTabActive, handleClockInNotify]);

  const closeSuccessModal = useCallback(() => {
    setNotificationState(prev => ({ ...prev, successModalOpen: false }));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const closeErrorModal = useCallback(() => {
    setNotificationState(prev => ({ ...prev, errorModalOpen: false }));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return {
    notificationState,
    closeSuccessModal,
    closeErrorModal,
  };
};
